import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorMessage, Loading, ModuleHeader } from '../../components/ui';
import { IconChevronDown, IconLink, IconSearch, IconShield } from '../../components/icons';
import { DSFA_LABEL, DsfaItem, DsfaStatus, useDsfa } from '../../lib/dsfa';

const STATUSES: DsfaStatus[] = ['red', 'yellow', 'green'];

const HINT: Record<DsfaStatus, string> = {
  red: 'DSFA nötig',
  yellow: 'begründen',
  green: 'unkritisch',
};

const titled = (status: DsfaStatus) => DSFA_LABEL[status][0].toUpperCase() + DSFA_LABEL[status].slice(1);

/**
 * Halbkreis-Anzeige mit allen drei Status: Die Bögen stehen im Verhältnis der
 * Anzahl. `pathLength="100"` macht aus Anteilen direkt Strichlängen.
 */
function StatusGauge({ counts, total }: { counts: Record<DsfaStatus, number>; total: number }) {
  const gap = total > 1 ? 1.2 : 0;
  let offset = 0;
  return (
    <svg
      className="dsfa-gauge"
      viewBox="0 0 160 100"
      role="img"
      aria-label={`${total} Einstellungen: ${STATUSES.map((s) => `${counts[s]} ${DSFA_LABEL[s]}`).join(', ')}`}
    >
      <path d="M16 88 A64 64 0 0 1 144 88" pathLength="100" className="dsfa-gauge-track" />
      {total > 0 &&
        STATUSES.map((status) => {
          const share = (counts[status] / total) * 100;
          if (share === 0) return null;
          const length = Math.max(share - gap, 0.1);
          const element = (
            <path
              key={status}
              d="M16 88 A64 64 0 0 1 144 88"
              pathLength="100"
              className={`dsfa-gauge-arc ${status}`}
              strokeDasharray={`${length} ${100 - length}`}
              strokeDashoffset={-offset}
            />
          );
          offset += share;
          return element;
        })}
      <text x="80" y="72" textAnchor="middle" className="dsfa-gauge-value">
        {total}
      </text>
      <text x="80" y="90" textAnchor="middle" className="dsfa-gauge-label">
        Einstellungen
      </text>
    </svg>
  );
}

function SettingRow({ item, open, onToggle }: { item: DsfaItem; open: boolean; onToggle: () => void }) {
  return (
    <div className={`dsfa-row ${open ? 'open' : ''}`}>
      <button type="button" className="dsfa-row-head" aria-expanded={open} onClick={onToggle}>
        <span className={`dsfa-dot ${item.status}`} aria-hidden="true" />
        <span className="dsfa-row-label">
          {item.label}
          <span className="sr-only"> – Status {DSFA_LABEL[item.status]}</span>
        </span>
        <span className="dsfa-chip-value">{item.display}</span>
        <IconChevronDown width={14} height={14} className="dsfa-row-chevron" />
      </button>
      {open && (
        <div className="dsfa-row-detail">
          <p>
            <strong>Status {DSFA_LABEL[item.status]}:</strong> {item.reason}
          </p>
          <p className="subtle">{item.why}</p>
          <Link to={item.where.to} className="dsfa-link">
            <IconLink width={13} height={13} /> {item.where.label}
          </Link>
        </div>
      )}
    </div>
  );
}

/**
 * DSFA-Report: kritische Einstellungen mit Datenschutz-Status.
 *
 * Reine Anzeige. Geändert wird an dem Ort, an dem die Einstellung ohnehin
 * steht; dort trägt sie eine Plakette (`DsfaBadge`) mit demselben Status.
 */
export function DsfaReportPage() {
  const { report, failed } = useDsfa();
  const [filter, setFilter] = useState<'all' | DsfaStatus>('all');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [openRows, setOpenRows] = useState<Set<string>>(new Set());

  const groups = useMemo(() => {
    if (!report) return [];
    const text = query.trim().toLowerCase();
    const matches = (item: DsfaItem) =>
      (filter === 'all' || item.status === filter) &&
      (!text || `${item.label} ${item.why} ${item.reason} ${item.display}`.toLowerCase().includes(text));
    return Array.from(new Set(report.items.map((item) => item.group)))
      .map((group) => ({
        name: group,
        all: report.items.filter((item) => item.group === group),
        shown: report.items.filter((item) => item.group === group && matches(item)),
      }))
      .filter((group) => group.shown.length > 0);
  }, [report, filter, query]);

  if (failed) return <ErrorMessage error={new Error('Der DSFA-Report konnte nicht geladen werden.')} />;
  if (!report) return <Loading />;

  const total = report.items.length;
  const counts = report.summary;
  const attention = STATUSES.slice(0, 2).flatMap((status) => report.items.filter((item) => item.status === status));
  const flip = (set: Set<string>, key: string) => {
    const next = new Set(set);
    if (!next.delete(key)) next.add(key);
    return next;
  };
  const pick = (status: DsfaStatus) => setFilter(filter === status ? 'all' : status);

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconShield width={22} height={22} />}
        title="DSFA-Report"
        subtitle="Kritische Einstellungen mit Datenschutz-Status. Geändert wird am ursprünglichen Ort; dort zeigt eine Plakette denselben Status."
        pill="Datenschutz"
        tone="info"
      />

      <div className="dsfa-toolbar">
        <label className="dsfa-search">
          <IconSearch width={15} height={15} />
          <input
            type="search"
            placeholder="Einstellung suchen"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            aria-label="Einstellung suchen"
          />
        </label>
        <div className="dsfa-filters" role="group" aria-label="Nach Status filtern">
          <button type="button" className={`dsfa-filter ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>
            Alle {total}
          </button>
          {STATUSES.map((status) => (
            <button key={status} type="button" className={`dsfa-filter ${filter === status ? 'on' : ''}`} onClick={() => pick(status)}>
              <span className={`dsfa-dot ${status}`} aria-hidden="true" />
              {titled(status)} {counts[status]}
            </button>
          ))}
        </div>
      </div>

      <div className="dsfa-hero">
        <section className="dsfa-card dsfa-overview">
          <h2>Datenschutz-Gesamtstand</h2>
          <div className="dsfa-overview-body">
            <StatusGauge counts={counts} total={total} />
            <ul className="dsfa-legend">
              {STATUSES.map((status) => (
                <li key={status}>
                  <span className={`dsfa-dot ${status}`} aria-hidden="true" />
                  <span>{titled(status)}</span>
                  <strong>{counts[status]}</strong>
                </li>
              ))}
            </ul>
          </div>
          <p className="small subtle">
            Arbeitshilfe nach der DSFA-Analyse (<code>docs/datenschutz</code>), keine Rechtsauskunft.
          </p>
        </section>

        <div className="dsfa-kpis">
          {STATUSES.map((status) => (
            <button key={status} type="button" className={`dsfa-card dsfa-kpi ${filter === status ? 'on' : ''}`} onClick={() => pick(status)}>
              <span className="dsfa-kpi-label">
                <span className={`dsfa-dot ${status}`} aria-hidden="true" />
                {titled(status)}
              </span>
              <span className="dsfa-kpi-value">
                {counts[status]} <small>{HINT[status]}</small>
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="dsfa-main">
        <div className="dsfa-groups">
          {groups.length === 0 && <div className="dsfa-card small subtle">Keine Einstellung passt zu Suche und Filter.</div>}
          {groups.map((group) => {
            const isClosed = collapsed.has(group.name);
            return (
              <section key={group.name} className="dsfa-card">
                <button
                  type="button"
                  className="dsfa-group-head"
                  aria-expanded={!isClosed}
                  onClick={() => setCollapsed(flip(collapsed, group.name))}
                >
                  <span className="dsfa-group-title">{group.name}</span>
                  <span className="small subtle">
                    {group.shown.length === group.all.length
                      ? `${group.all.length} Einstellungen`
                      : `${group.shown.length} von ${group.all.length}`}
                  </span>
                  <IconChevronDown width={16} height={16} className={`dsfa-group-chevron ${isClosed ? 'closed' : ''}`} />
                </button>
                <div className="dsfa-bar" aria-hidden="true">
                  {STATUSES.map((status) => {
                    const n = group.all.filter((item) => item.status === status).length;
                    return n > 0 ? <span key={status} className={status} style={{ flex: n }} /> : null;
                  })}
                </div>
                {!isClosed && (
                  <div className="dsfa-rows">
                    {group.shown.map((item) => (
                      <SettingRow
                        key={item.key}
                        item={item}
                        open={openRows.has(item.key)}
                        onToggle={() => setOpenRows(flip(openRows, item.key))}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <aside className="dsfa-card dsfa-attention">
          <h2>Handlungsbedarf</h2>
          <p className="small subtle">Rot zuerst, dann Gelb</p>
          {attention.length === 0 ? (
            <p className="small">Keine Einstellung ist rot oder gelb.</p>
          ) : (
            <ul>
              {attention.map((item) => (
                <li key={item.key}>
                  <span className={`dsfa-dot ${item.status}`} aria-hidden="true" />
                  <div>
                    <strong>{item.label}</strong>
                    <div className="small subtle">{item.display}</div>
                  </div>
                  <Link to={item.where.to} className="dsfa-go" title={item.where.label} aria-label={`${item.label}: ${item.where.label}`}>
                    <IconLink width={14} height={14} />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
